export type ReportColumn = {
  key: string;
  label: string;
  kind: 'dimension' | 'measure';
  axis?: 'row' | 'column';
  fn?: string;
};

export type PivotDisplayColumn = {
  key: string;
  label: string;
  kind: 'dimension' | 'measure';
  group?: string;
  fn?: string;
  colEquals?: Record<string, unknown>;
};

export type PivotDisplay = {
  columns: PivotDisplayColumn[];
  rows: Array<Record<string, unknown>>;
  columnGroups: Array<{ key: string; label: string; span: number }>;
  columnAxisTitle?: string;
  measureCount: number;
};

export function formatPivotCell(value: unknown): string {
  if (value == null || value === '') return '—';
  if (typeof value === 'bigint') return value.toString();
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
    return value.slice(0, 10);
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Number.isInteger(value)
      ? String(value)
      : value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }
  return String(value);
}

export function pivotGroupLabel(value: unknown, fallback?: string): string {
  const text = formatPivotCell(value);
  return text === '—' ? fallback || '(blank)' : text;
}

function dimDisplayValue(row: Record<string, unknown>, key: string): unknown {
  const labeled = row[`${key}__label`];
  if (labeled != null && labeled !== '') return labeled;
  return row[key];
}

function combineValues(fn: string | undefined, values: unknown[]): unknown {
  const present = values.filter((v) => v != null && v !== '');
  if (!present.length) return null;
  if (fn === 'min') return present.reduce((a, b) => (String(a) < String(b) ? a : b));
  if (fn === 'max') return present.reduce((a, b) => (String(a) > String(b) ? a : b));
  const nums = present.map(Number);
  if (!nums.every((n) => Number.isFinite(n))) return null;
  if (fn === 'avg') {
    return Math.round((nums.reduce((a, b) => a + b, 0) / nums.length) * 100) / 100;
  }
  return nums.reduce((a, b) => a + b, 0);
}

function rowKeyOf(row: Record<string, unknown>, keys: string[]): string {
  return JSON.stringify(keys.map((k) => row[k] ?? null));
}

export function pivotReport(
  result: { columns: ReportColumn[]; rows: Array<Record<string, unknown>> },
  opts?: { showTotals?: boolean },
): PivotDisplay {
  const showTotals = Boolean(opts?.showTotals);
  const rowCols = result.columns.filter(
    (c) => c.kind === 'dimension' && c.axis !== 'column',
  );
  const colCols = result.columns.filter((c) => c.kind === 'dimension' && c.axis === 'column');
  const measures = result.columns.filter((c) => c.kind === 'measure');

  if (!colCols.length) {
    const columns: PivotDisplayColumn[] = result.columns.map((c) => ({
      key: c.key,
      label: c.label,
      kind: c.kind,
      fn: c.fn,
    }));
    const rows = [...result.rows];
    if (showTotals && rows.length && measures.length) {
      const total: Record<string, unknown> = { __total: true };
      rowCols.forEach((c, i) => {
        total[c.key] = i === 0 ? 'Total' : '';
      });
      for (const m of measures) {
        total[m.key] = combineValues(
          m.fn,
          rows.map((r) => r[m.key]),
        );
      }
      rows.push(total);
    }
    return { columns, rows, columnGroups: [], measureCount: measures.length };
  }

  const groups = new Map<string, Record<string, unknown>>();
  for (const row of result.rows) {
    const equals: Record<string, unknown> = {};
    const labels: string[] = [];
    for (const c of colCols) {
      equals[c.key] = row[c.key];
      labels.push(pivotGroupLabel(dimDisplayValue(row, c.key)));
    }
    const key = JSON.stringify(colCols.map((c) => row[c.key] ?? null));
    if (!groups.has(key)) {
      groups.set(key, { key, label: labels.join(' · '), equals });
    }
  }
  const groupList = Array.from(groups.values()).sort((a, b) => {
    const aBlank = String(a.label) === '(blank)';
    const bBlank = String(b.label) === '(blank)';
    if (aBlank !== bBlank) return aBlank ? 1 : -1;
    return String(a.label).localeCompare(String(b.label));
  });

  const columns: PivotDisplayColumn[] = rowCols.map((c) => ({
    key: c.key,
    label: c.label,
    kind: 'dimension' as const,
  }));
  const columnAxisTitle = colCols.map((c) => c.label).filter(Boolean).join(' · ');
  const columnGroups: Array<{ key: string; label: string; span: number }> = rowCols.length
    ? [{ key: '_rows', label: rowCols.map((c) => c.label).join(' · '), span: rowCols.length }]
    : [];

  for (const g of groupList) {
    columnGroups.push({
      key: String(g.key),
      label: String(g.label),
      span: Math.max(1, measures.length),
    });
    for (const m of measures) {
      columns.push({
        key: `p:${g.key}::${m.key}`,
        label: measures.length > 1 ? m.label : String(g.label),
        kind: 'measure',
        group: String(g.label),
        fn: m.fn,
        colEquals: g.equals as Record<string, unknown>,
      });
    }
  }
  if (showTotals && measures.length) {
    columnGroups.push({ key: '_total', label: 'Total', span: measures.length });
    for (const m of measures) {
      columns.push({
        key: `t::${m.key}`,
        label: measures.length > 1 ? m.label : 'Total',
        kind: 'measure',
        group: 'Total',
        fn: m.fn,
      });
    }
  }

  const rowMap = new Map<string, Record<string, unknown>>();
  const rowOrder: string[] = [];
  for (const row of result.rows) {
    const rk = rowKeyOf(row, rowCols.map((c) => c.key));
    if (!rowMap.has(rk)) {
      const next: Record<string, unknown> = {};
      for (const c of rowCols) {
        next[c.key] = dimDisplayValue(row, c.key);
        if (row[`${c.key}__label`] != null) next[`${c.key}__label`] = row[`${c.key}__label`];
        if (row[`${c.key}__id`] != null) next[`${c.key}__id`] = row[`${c.key}__id`];
      }
      rowMap.set(rk, next);
      rowOrder.push(rk);
    }
    const dest = rowMap.get(rk)!;
    const gk = JSON.stringify(colCols.map((c) => row[c.key] ?? null));
    for (const m of measures) {
      dest[`p:${gk}::${m.key}`] = row[m.key];
    }
  }

  const rows = rowOrder.map((rk) => {
    const row = rowMap.get(rk)!;
    if (showTotals) {
      for (const m of measures) {
        row[`t::${m.key}`] = combineValues(
          m.fn,
          groupList.map((g) => row[`p:${g.key}::${m.key}`]),
        );
      }
    }
    return row;
  });

  if (showTotals && rows.length && measures.length) {
    const total: Record<string, unknown> = { __total: true };
    rowCols.forEach((c, i) => {
      total[c.key] = i === 0 ? 'Total' : '';
    });
    for (const col of columns.filter((c) => c.kind === 'measure')) {
      total[col.key] = combineValues(
        col.fn,
        rows.map((r) => r[col.key]),
      );
    }
    rows.push(total);
  }

  return { columns, rows, columnGroups, columnAxisTitle, measureCount: measures.length };
}
